// Figma 생성물 위에 콘텐츠 카드를 채우는 공용 도구.
//
// 메인(MediaHubNewView)과 목록(MediaListNewView)이 같은 카드 구조를 쓴다.
// 두 화면 모두 Figma 생성 컴포넌트를 손대지 않고 data-node-id 로 집어서 채우므로,
// 채워 넣는 규칙만 여기로 모았다.

// 썸네일이 비어 있는 콘텐츠(네이버 블로그 글 등)에 쓸 기본 이미지.
export const FALLBACK_THUMB = '/images/content-placeholder.png'

// Figma 뱃지는 "블로그 / 유튜브 / 뉴스" 같은 한글이고 API 는 sourceType 코드로 온다.
const SOURCE_LABEL = {
  NAVER_BLOG: '블로그',
  BLOG: '블로그',
  YOUTUBE: '유튜브',
  YOUTUBE_SHORTS: '클립',
  NAVER_TV: '네이버TV',
  NEWS: '뉴스',
  INSTAGRAM: 'SNS',
}

// 소스 필터 칩의 차례는 **관리자가 정한다** — 관리자 > 미디어허브 > 카테고리의
// "노출 순서"(media_hub_categories.expose_order)다. 서버가 그 차례로 내려 주고
// (mediahub-server 의 selectCategorySourceFilters), 여기서는 혹시 몰라 한 번 더
// 그 값으로 줄 세운다. 값이 같거나 없으면 서버가 준 차례를 그대로 둔다 —
// Array.sort 가 안정 정렬이다.
//
// 2026-10-07 에 화면마다 이름을 박아 넣는 대신 이 방식으로 바꿨다. 소스가 늘면
// 관리자에서 숫자만 고치면 된다(치지직 · 네이버카페 · 네이버스토어 · 인스타그램).
// 화면마다 따로 정렬하면 한쪽만 고치게 되므로 여기 한 군데에 둔다.
const orderOf = (f) => {
  const n = Number(f?.exposeOrder)
  return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER
}

export const sortSourceFilters = (filters = []) =>
  [...filters].sort((a, b) => orderOf(a) - orderOf(b))

export const sourceLabel = (item) =>
  SOURCE_LABEL[item.sourceType] || item.mediaHubCategoryName || item.contentType || ''

// 기존 화면과 같은 표기 (HomeView.vue 의 formatDate)
export const formatDate = (value) => {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const p = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}.${p(d.getMonth() + 1)}.${p(d.getDate())}`
}

// Figma 는 고정 높이 텍스트 칸인데 API 문구는 그보다 훨씬 길 수 있다
// (네이버TV 설명에는 해시태그가 수백 자 들어온다). 그대로 넣으면 카드를 넘쳐 레이아웃이 깨진다.
// lines 를 주면 그 줄 수로 고정한다. 한 줄에 몇 자든 카드 높이가 같아져 윗줄이 맞는다.
// 주지 않으면 원래 칸 높이에서 역산한다(큰 카드처럼 칸이 넉넉한 경우).
export const clampToBox = (el, text, lines = 0) => {
  if (!el) return
  if (!lines) {
    const cs = window.getComputedStyle(el)
    const lineHeight = parseFloat(cs.lineHeight) || parseFloat(cs.fontSize) * 1.4
    lines = Math.max(1, Math.round(el.getBoundingClientRect().height / lineHeight)) || 1
  }
  el.textContent = text
  el.style.display = '-webkit-box'
  el.style.webkitBoxOrient = 'vertical'
  el.style.webkitLineClamp = String(lines)
  el.style.overflow = 'hidden'
  el.style.whiteSpace = 'normal'
  el.style.wordBreak = 'break-word'
  // 글이 짧거나 비어도 칸을 유지해야 카드 높이가 서로 같아진다.
  const cs2 = window.getComputedStyle(el)
  const lh = parseFloat(cs2.lineHeight) || parseFloat(cs2.fontSize) * 1.4
  el.style.minHeight = `${Math.round(lh * lines)}px`
}

// 썸네일 교체. Figma 가 넣어 둔 크롭(h-133.5% / top--16.75% 같은 값)을 걷어내고
// object-cover 로 칸을 채운다. 안 그러면 비율이 다른 이미지가 엉뚱하게 잘린다.
export const setThumb = (img, url) => {
  if (!img) return
  img.src = url || FALLBACK_THUMB
  img.style.height = '100%'
  img.style.width = '100%'
  img.style.top = '0'
  img.style.left = '0'
  img.style.objectFit = 'cover'
  // 원본이 사라졌거나 외부 도메인이 막힌 경우에도 빈 칸이 남지 않게 한다.
  img.onerror = () => {
    img.onerror = null
    img.src = FALLBACK_THUMB
  }
}

// 카드 글 채우기. 카드마다 <p> 개수가 4·5·6·9 로 다른데 규칙은 같다.
//   [0]        소스 뱃지
//   [1]        제목 (Figma 가 2줄로 쪼개 둔 경우가 있어 다음 칸은 비운다)
//   [n-3]      설명 (칸이 있을 때만)
//   [n-2][n-1] 작성자 · 날짜
export const fillCardText = (ps, item, { desc = true, titleLines = 2 } = {}) => {
  const n = ps.length
  if (n < 3) return
  const big = n > 6
  const label = sourceLabel(item)
  if (label) ps[0].textContent = label
  clampToBox(ps[1], item.title || '', big ? 0 : titleLines)
  // Figma 가 제목을 2줄로 쪼갠 카드는 둘째 줄 칸이 남는다.
  // 설명이 들어가는 5칸 카드에서는 그 자리가 곧 설명 칸이므로 비우지 않는다.
  if (ps[2] && (n >= 6 || (n === 5 && !desc))) ps[2].textContent = ''
  if (desc) {
    // <p> 가 6개 이상이면 설명은 뒤에서 세 번째, 5개짜리 카드는 제목 둘째 줄 자리를 쓴다.
    const descEl = n >= 6 ? ps[n - 3] : ps[2]
    if (descEl) clampToBox(descEl, item.description || '', big ? 0 : 2)
  }
  ps[n - 2].textContent = item.authorName || item.mediaSourceName || item.mediaHubCategoryName || ''
  ps[n - 1].textContent = formatDate(item.publishedDt || item.fetchedDt)
}

// 제목 위치로 부드럽게 이동.
//
// scroll-behavior: smooth 가 통째로 무시되는 환경이 있다(에디터 내장 미리보기 등).
// 그런 데서는 눌러도 아무 일도 안 일어나 고장으로 보이므로,
// 잠깐 뒤에도 그대로면 그냥 옮긴다.
export const scrollToAnchor = (anchor, margin = 24) => {
  const scroller = document.scrollingElement || document.documentElement
  const before = scroller.scrollTop
  const target = Math.max(0, anchor.getBoundingClientRect().top + before - margin)
  anchor.scrollIntoView({ behavior: 'smooth', block: 'start' })
  window.setTimeout(() => {
    if (Math.abs(scroller.scrollTop - before) < 2) scroller.scrollTop = target
  }, 150)
}

// Figma 상자에는 문구만 있고 input 이 없다. 같은 자리에 끼워 넣는다.
// 반환값은 되돌리기 함수다.
export const mountSearchInput = (hint, onSubmit) => {
  const style = window.getComputedStyle(hint)
  const input = document.createElement('input')
  input.type = 'text'
  input.placeholder = hint.textContent.trim()
  input.setAttribute('aria-label', '미디어 검색')
  // 글자 모양은 Figma 문구 그대로 가져오고, 나머지는 인라인으로 못 박는다.
  // (Tailwind 클래스로 주면 생성물에 없는 유틸이라 CSS 가 안 나올 수 있다)
  Object.assign(input.style, {
    fontFamily: style.fontFamily,
    fontSize: style.fontSize,
    fontWeight: style.fontWeight,
    lineHeight: style.lineHeight,
    color: '#202632',
    flex: '1 1 0%',
    minWidth: '0',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    padding: '0',
  })
  const submit = () => {
    const keyword = input.value.trim()
    if (!keyword) {
      input.focus()
      return
    }
    onSubmit(keyword)
  }
  const onKeydown = (e) => {
    if (e.key === 'Enter') submit()
  }
  input.addEventListener('keydown', onKeydown)
  hint.style.display = 'none'
  hint.parentNode.insertBefore(input, hint)
  return {
    input,
    submit,
    destroy: () => {
      input.removeEventListener('keydown', onKeydown)
      input.remove()
      hint.style.display = ''
    },
  }
}

// Figma 콤보박스는 글자 + 화살표 아이콘만 있는 상자다. 진짜 select 를 겹쳐 둔다.
// 보이는 글자는 Figma 것을 그대로 쓰고, select 자체는 투명하게 덮어씌운다.
export const mountSelect = (box, labelEl, options, value, onChange) => {
  const select = document.createElement('select')
  options.forEach((o) => {
    const opt = document.createElement('option')
    opt.value = String(o.value)
    opt.textContent = o.label
    select.appendChild(opt)
  })
  select.value = String(value)
  select.setAttribute('aria-label', labelEl.textContent.trim())
  Object.assign(select.style, {
    position: 'absolute',
    inset: '0',
    width: '100%',
    height: '100%',
    opacity: '0',
    cursor: 'pointer',
    // 네이티브 목록이 상자 위치에 뜨도록 실제로 덮어 둔다(숨기면 목록이 엉뚱한 곳에 뜬다).
    appearance: 'none',
    border: 'none',
    background: 'transparent',
  })
  const sync = () => {
    const picked = options.find((o) => String(o.value) === select.value)
    if (picked) labelEl.textContent = picked.label
  }
  const onInput = () => {
    sync()
    onChange(select.value)
  }
  select.addEventListener('change', onInput)
  const prevPosition = box.style.position
  if (window.getComputedStyle(box).position === 'static') box.style.position = 'relative'
  box.appendChild(select)
  sync()
  return () => {
    select.removeEventListener('change', onInput)
    select.remove()
    box.style.position = prevPosition
  }
}
